import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useSearch } from '@tanstack/react-router'
import type { JSX } from 'react'

import { TV_PAGE_HEIGHT } from '../../components/tv-canvas.tsx'
import { Notice } from '../../design-system/notice.tsx'
import { Skeleton } from '../../design-system/skeleton.tsx'
import { api } from '../../lib/api.ts'
import { ApiRequestError, toApiError } from '../../lib/api-error.ts'
import { QUIET_QUERY_META } from '../../lib/query-activity.ts'
import { DashboardGrid } from './-components/dashboard-grid.tsx'
import type { DashboardBody } from './-lib/dashboard-shape.ts'
import { readDashboard } from './-lib/dashboard-read.ts'

const MISSING = 'This dashboard is not shared, or it was removed.'
const UNREADABLE = 'This dashboard could not be read.'
const TV_DASHBOARD_REFRESH_MS = 5 * 60 * 1000

/**
 * The widest 16:9 grid whose height fits the room the TV canvas gives.
 *
 * The grid takes its height from its width. A wider layout cannot shrink it,
 * so the canvas could not fit a tall grid by scaling. Outside the TV the
 * property is not set, the fallback is wider than any screen, and the grid
 * keeps its full width.
 */
const FIT_WIDTH = `calc(var(${TV_PAGE_HEIGHT}, 100vw) * 16 / 9)`

/**
 * One shared TV dashboard. The document is the `id` search param, read loosely
 * so a typed `validateSearch` does not narrow every `navigate` call in the app.
 */
export const Route = createFileRoute('/studio/tv')({
  component: TvPage
})

function TvPage(): JSX.Element {
  const search: Record<string, unknown> = useSearch({ strict: false })
  const id =
    typeof search.id === 'string' && search.id !== '' ? search.id : null
  return <TvScreen id={id} />
}

/**
 * Fetch the shared dashboard and draw it. Nothing here is an editor.
 *
 * A disabled query stays pending, so a missing id is decided before that.
 *
 * @param props - Which dashboard.
 * @param props.id - The document id from the address, or null.
 * @returns The screen.
 */
function TvScreen({ id }: { id: string | null }): JSX.Element {
  const query = useQuery({
    queryKey: ['display-dashboard', id],
    meta: QUIET_QUERY_META,
    queryFn: () => fetchDisplayDashboard(id ?? ''),
    enabled: id !== null,
    refetchInterval: TV_DASHBOARD_REFRESH_MS,
    retry: (failureCount, error) =>
      !(error instanceof ApiRequestError && error.status === 404) &&
      failureCount < 3
  })

  if (id === null) return <Notice data-testid="tv-missing">{MISSING}</Notice>
  if (query.isPending) {
    return <Skeleton data-testid="tv-loading" className="h-full w-full" />
  }
  if (query.error instanceof ApiRequestError && query.error.status === 404) {
    return <Notice data-testid="tv-missing">{MISSING}</Notice>
  }
  if (query.isError || !query.data) {
    return <Notice data-testid="tv-unreadable">{UNREADABLE}</Notice>
  }

  return (
    <div
      data-testid="tv-screen"
      className="flex h-full min-h-0 w-full items-center justify-center"
    >
      <div
        data-testid="tv-grid-frame"
        className="w-full"
        style={{ maxWidth: FIT_WIDTH }}
      >
        <DashboardGrid body={query.data} />
      </div>
    </div>
  )
}

/**
 * @param id - The dashboard id.
 * @returns The widgets worth drawing.
 * @throws {ApiRequestError} when the service refuses, including a dashboard that is not shared.
 * @throws {Error} when the body arrived and cannot be read as a dashboard.
 */
async function fetchDisplayDashboard(id: string): Promise<DashboardBody> {
  let payload: unknown
  try {
    payload = await api
      .get('display-dashboard', { searchParams: { id } })
      .json()
  } catch (error) {
    throw await toApiError(error)
  }
  const dashboard = isRecord(payload) ? payload.dashboard : null
  const body = readDashboard(dashboard)
  if (!body) throw new Error(UNREADABLE)
  return body
}

/**
 * @param value - Any value.
 * @returns Whether it is a plain object.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
